import { Component, inject, OnDestroy, OnInit, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MetabaseService } from '../../../../services/metabase.service';
import { UserProfileService } from '../../../../services/user/user-profile.service';
import { environment } from '../../../../../environments/environment';

/** How long the dashboard may take before the page offers a reload. */
const SLOW_AFTER_MS = 20000;

@Component({
  selector: 'app-ussd-reports',
  standalone: true,
  imports: [],
  templateUrl: './reports.component.html',
  styleUrl: './reports.component.css',
})
export class UssdReportsComponent implements OnInit, OnDestroy {
  private sanitizer = inject(DomSanitizer);
  private metabaseService = inject(MetabaseService);
  private userService = inject(UserProfileService);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  iframeUrl = signal<SafeResourceUrl | null>(null);
  loaded = signal(false);
  slow = signal(false);
  /** Bumped by "Reload" so the iframe is created again. */
  attempt = signal(0);

  private slowTimer?: ReturnType<typeof setTimeout>;

  ngOnInit() {
    // The dashboard loads in the browser only; the server render shows the placeholder.
    if (!this.isBrowser) return;

    const orgId = this.userService.jwtOrgId?.trim();
    const base = `${environment.metabase.ussdPublicAnalyticsUrl}/public/dashboard/${environment.metabase.ussdPublicDashboardUuid}`;

    const params = new URLSearchParams();
    if (orgId) {
      params.set('organisation_id', this.metabaseService.uuidToBase64(orgId));
    }

    // Metabase display options: our header replaces its title and border,
    // and the night theme follows the console's dark mode.
    const dark = !!document.querySelector('.dark');
    const hash = ['hide_parameters=organisation_id', 'bordered=false', 'titled=false'];
    if (dark) hash.push('theme=night');

    const query = params.toString();
    const url = `${base}${query ? `?${query}` : ''}#${hash.join('&')}`;
    this.iframeUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(url));
    this.startTimer();
  }

  ngOnDestroy(): void {
    clearTimeout(this.slowTimer);
  }

  onLoad(): void {
    this.loaded.set(true);
    this.slow.set(false);
    clearTimeout(this.slowTimer);
  }

  reload(): void {
    this.loaded.set(false);
    this.slow.set(false);
    this.attempt.update((n) => n + 1);
    this.startTimer();
  }

  private startTimer(): void {
    clearTimeout(this.slowTimer);
    this.slowTimer = setTimeout(() => {
      if (!this.loaded()) this.slow.set(true);
    }, SLOW_AFTER_MS);
  }
}
